import { useRef, useState, type ChangeEvent, type DragEvent } from "react";

import { Button } from "@/components/ui/Button";
import { useUploadCoi } from "./api";

// Phase 2b coi-upload-and-review FR-1 — drag-drop + file-picker
// uploader. Accepts PDF + JPG/PNG/HEIC; rejects anything else before
// hitting the wire so customers see the message immediately.
// MAX_BYTES mirrors the server cap (FR-12 / AC-12).

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic";
const MAX_BYTES = 25 * 1024 * 1024;

interface Props {
  onUploaded: (coiId: number) => void;
}

export function CoiUploader({ onUploaded }: Props): JSX.Element {
  const upload = useUploadCoi();
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function clientValidate(file: File): string | null {
    if (file.size > MAX_BYTES) return "File is larger than 25 MB.";
    if (!/(pdf|jpe?g|png|heic)$/i.test(file.name)) {
      return "Only PDF, JPG, PNG, or HEIC files are accepted.";
    }
    return null;
  }

  async function pick(file: File): Promise<void> {
    setLocalError(null);
    const reason = clientValidate(file);
    if (reason) {
      setLocalError(reason);
      return;
    }
    try {
      const doc = await upload.mutateAsync(file);
      onUploaded(doc.id);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "Upload failed.");
    }
  }

  function onChange(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file) void pick(file);
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void pick(file);
  }

  // Audit P6 — dashed-zinc-300 1px → brand-red 2px on hover + drag.
  // The dropzone is the page's most important action; the audit
  // flagged it as visually meek for the role it plays.
  const dropClasses = dragging
    ? "border-brand-red bg-brand-red/5"
    : "border-zinc-300 bg-zinc-50 hover:border-brand-red hover:bg-brand-red/5";
  const dropClass = `flex flex-col items-center justify-center rounded-lg border-2 border-dashed ${dropClasses} px-4 py-12 transition cursor-pointer`;

  return (
    <div className="space-y-4">
      <div
        className={dropClass}
        data-testid="coi-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <p className="text-sm text-zinc-700">Drag a PDF or image here, or</p>
        <Button
          variant="outline"
          className="mt-3"
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
          disabled={upload.isPending}
        >
          {upload.isPending ? "Uploading…" : "Choose file"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={onChange}
          className="sr-only"
          aria-label="Select certificate of insurance"
        />
      </div>

      {/* Audit P6 — surface accepted file types + max size so customers
          aren't guessing. MAX_BYTES above is the actual server cap. */}
      <p className="text-xs text-zinc-500" data-testid="coi-upload-microcopy">
        PDF, JPG, PNG, or HEIC · 25 MB max
      </p>

      <p className="text-xs text-zinc-600">
        Required: General Liability ≥ $1M each occurrence, JDR named as
        additional insured and loss payee.
      </p>

      {/* Audit P6 — first-time customers ask "what's an ACORD 25?"
          before uploading. Inline disclosure heads that off. */}
      <details className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm" data-testid="coi-acord-25-helper">
        <summary className="cursor-pointer text-brand-red hover:text-brand-red-dark">
          What&rsquo;s an ACORD 25?
        </summary>
        <p className="mt-2 text-zinc-700">
          An ACORD 25 is the standardized one-page certificate your
          insurance agent or broker issues to prove your General
          Liability coverage. Ask your agent for one with JDR named as
          additional insured and loss payee on the certificate holder
          and remarks sections.
        </p>
      </details>

      {localError && (
        <div role="alert" className="rounded border border-danger/40 bg-danger/5 px-3 py-2 text-sm text-danger">
          {localError}
        </div>
      )}
    </div>
  );
}
